import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Container,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import { useLanguage } from '../i18n/LanguageContext';

import productImg from "../imaged/pro.jpg"; 
import heroBg from "../imaged/personal care.jpg";
import SolutionsPage from "./SolutionsPage";

const MotionBox = motion(Box);

const ProductPage = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [tab, setTab] = useState(0);

  const CATEGORY_KEYS = ["Healthcare", "Personal Care", "Medicine", "Nutraceuticals"];
  const CATEGORIES = CATEGORY_KEYS.map(key => t(`products.categories.${key}`));

  // ===== DATA =====
  const PRODUCTS = [
    { 
      name: t('products.items.hairOil'), 
      desc: t('products.data.Hair Oil.desc'), 
      image: productImg, 
      category: t('products.categories.Personal Care'), 
      bullets: t('products.data.Hair Oil.bullets', { returnObjects: true }) 
    },
    { 
      name: t('products.items.painOil'), 
      desc: t('products.data.Painkiller Oil.desc'), 
      image: productImg, 
      category: t('products.categories.Medicine'), 
      bullets: t('products.data.Painkiller Oil.bullets', { returnObjects: true }) 
    },
    { 
      name: t('products.items.dantManjan'), 
      desc: t('products.data.Dant Manjan.desc'), 
      image: productImg, 
      category: t('products.categories.Personal Care'), 
      bullets: t('products.data.Dant Manjan.bullets', { returnObjects: true }) 
    },
    { 
      name: t('products.items.nasya'), 
      desc: t('products.data.Nasya.desc'), 
      image: productImg, 
      category: t('products.categories.Medicine'), 
      bullets: t('products.data.Nasya.bullets', { returnObjects: true }) 
    },
    { 
      name: t('products.items.diabetes'), 
      desc: t('products.data.Diabetes Care.desc'), 
      image: productImg, 
      category: t('products.categories.Healthcare'), 
      bullets: t('products.data.Diabetes Care.bullets', { returnObjects: true }) 
    },
    { 
      name: t('products.items.heartRelief'), 
      desc: t('products.data.Heart Relief (BP).desc'), 
      image: productImg, 
      category: t('products.categories.Healthcare'), 
      bullets: t('products.data.Heart Relief (BP).bullets', { returnObjects: true }) 
    },
    { 
      name: t('products.items.energyBooster'), 
      desc: t('products.data.Energy Booster.desc'), 
      image: productImg, 
      category: t('products.categories.Nutraceuticals'), 
      bullets: t('products.data.Energy Booster.bullets', { returnObjects: true }) 
    },
    { 
      name: t('products.items.npr'), 
      desc: t('products.data.NPR.desc'), 
      image: productImg, 
      category: t('products.categories.Nutraceuticals'), 
      bullets: t('products.data.NPR.bullets', { returnObjects: true }) 
    },
    { 
      name: t('products.items.jointCare'), 
      desc: t('products.data.Joint Care Churn.desc'), 
      image: productImg, 
      category: t('products.categories.Healthcare'), 
      bullets: t('products.data.Joint Care Churn.bullets', { returnObjects: true }) 
    },
    { 
      name: t('products.items.kidneyStone'), 
      desc: t('products.data.Kidney Stone Care.desc'), 
      image: productImg, 
      category: t('products.categories.Medicine'), 
      bullets: t('products.data.Kidney Stone Care.bullets', { returnObjects: true }) 
    },
    { 
      name: t('products.items.digestiveCare'), 
      desc: t('products.data.Digestive Care.desc'), 
      image: productImg, 
      category: t('products.categories.Healthcare'), 
      bullets: t('products.data.Digestive Care.bullets', { returnObjects: true }) 
    },
  ];

  const visibleProducts = showAll ? PRODUCTS : PRODUCTS.slice(0, 4);

  const openModal = (p) => {
    setSelected(p);
    setOpen(true);
  };

  return (
    <Box sx={{ backgroundColor: "#f7faf7", minHeight: "100vh" }}>
      {/* ===== HERO SECTION ===== */}
      <Box
        sx={{
          position: "relative",
          height: "85vh",
          minHeight: 600,
          backgroundImage: `url("${heroBg}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.8))"
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, textAlign: "center" }}>
          <MotionBox
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
             <Typography variant="overline" sx={{ color: "#81c784", letterSpacing: 4, fontWeight: 700, fontSize: "1.1rem", mb: 2, display: "block" }}>
              {t('products.hero.overline')}
            </Typography>
            <Typography variant="h1" sx={{ color: "white", fontWeight: 800, mb: 3, fontSize: { xs: "3rem", md: "5.5rem" }, lineHeight: 1.1 }}>
              {t('products.hero.title')}
            </Typography>
            <Typography variant="h5" sx={{ color: "rgba(255,255,255,0.95)", maxWidth: 800, mx: "auto", fontWeight: 300, fontSize: "1.4rem" }}>
              {t('products.hero.subtitle')}
            </Typography>
             <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={() => alert("Redirect to Contact Page")}
              sx={{ mt: 5, px: 5, py: 1.5, borderRadius: 50, fontSize: "1.1rem", fontWeight: 700 }}
            >
              {t('products.hero.cta')}
            </Button>
          </MotionBox>
        </Container>
      </Box>

      {/* Intro & Info */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="center">
           <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 5,
                border: "2px dashed #c8e6c9",
                borderRadius: 4,
                backgroundColor: "#f1f8e9",
                whiteSpace: "pre-line",
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0, height: '4px',
                  background: 'linear-gradient(90deg, #43a047, #66bb6a)'
                }
              }}
            >
              <Typography variant="h6" color="primary.main" gutterBottom fontWeight={700}>
                {t('products.quality.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary" lineHeight={1.8}>
                {t('products.quality.text')}
              </Typography>
            </Box>
           </Grid>
           <Grid item xs={12} md={6}>
              <Typography variant="body1" color="text.secondary" lineHeight={2} fontSize="1.1rem">
                {t('products.quality.intro')}
              </Typography>
           </Grid>
        </Grid>

        <Typography variant="h2" color="text.primary" sx={{ mt: 10, mb: 2, textAlign: "center", fontWeight: 800 }}>
          {t('products.title')}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 8, textAlign: "center", maxWidth: 600, mx: "auto" }}>
          {t('products.list.subtitle')}
        </Typography>

        <Grid container spacing={4}>
          {visibleProducts.map((p, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                onClick={() => openModal(p)}
                sx={{
                  height: 400,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  borderRadius: 4,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: '1px solid rgba(0,0,0,0.04)',
                  "&:hover": { 
                    transform: "translateY(-10px)", 
                    boxShadow: '0 12px 30px rgba(46, 125, 50, 0.2)' 
                  },
                }}
              >
                <Box sx={{ p: 3, pb: 0, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fbfdfb' }}>
                  <CardMedia
                    component="img"
                    image={p.image}
                    alt={p.name}
                    sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: "contain", filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}
                  />
                </Box>
                <CardContent sx={{ pt: 2, px: 3, pb: 3 }}>
                  <Typography variant="overline" color="success.main" fontWeight={700}>
                    {p.category}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'primary.dark', lineHeight: 1.2, mb: 1 }}>
                    {p.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      fontSize: '0.9rem',
                      lineHeight: 1.6
                    }}
                  >
                    {p.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* View More / View Less Toggle */}
        <Box textAlign="center" mt={4}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setShowAll(!showAll)}
            sx={{ borderRadius: 20, px: 4 }}
          >
            {showAll ? t('products.list.viewLess') : t('products.list.viewMore')}
          </Button>
        </Box>
      </Container>

      {/* ===== CATEGORY SECTION ===== */}
      <Container maxWidth="lg" sx={{ pb: 10 }}>
        <Typography variant="h4" textAlign="center" fontWeight={700} mb={4}>
          {t('products.list.solutionsTitle')}
        </Typography>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          centered
          sx={{ mb: 4 }}
          indicatorColor="primary"
          textColor="primary"
        >
          {CATEGORIES.map((c, idx) => (
            <Tab label={c} key={idx} sx={{ fontWeight: 600 }} />
          ))}
        </Tabs>

        <Grid container spacing={3}>
          {PRODUCTS.filter((p) => p.category === CATEGORIES[tab]).map((p, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                onClick={() => openModal(p)}
                sx={{
                  height: 360,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  borderRadius: 4,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: '1px solid rgba(0,0,0,0.04)',
                  "&:hover": { 
                    transform: "translateY(-8px)", 
                    boxShadow: '0 12px 30px rgba(46, 125, 50, 0.2)' 
                  },
                }}
              >
                <Box sx={{ p: 2, pb: 0, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fbfdfb' }}>
                  <CardMedia
                    component="img"
                    image={p.image}
                    alt={p.name}
                    sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: "contain", filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}
                  />
                </Box>
                <CardContent sx={{ pt: 2 }}>
                  <Typography variant="subtitle1" fontWeight={800} gutterBottom sx={{ color: 'primary.dark' }}>
                    {p.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                    {p.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ===== PRODUCT MODAL ===== */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Grid container>
            <Grid item xs={12} md={6} sx={{ bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                <Box
                  component="img"
                  src={selected?.image}
                  alt={selected?.name}
                  sx={{ width: "100%", maxHeight: 400, objectFit: "contain", filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.1))' }}
                />
            </Grid>
            <Grid item xs={12} md={6} sx={{ p: 5 }}>
              <Typography variant="overline" color="success.main" fontWeight={700}>
                {selected?.category}
              </Typography>
              <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: 'primary.dark', mt: 1 }}>
                {selected?.name}
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                {selected?.desc}
              </Typography>
              
              <Box sx={{ mt: 3, mb: 4 }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>{t('products.modal.keyBenefits')}</Typography>
                  {selected?.bullets && Array.isArray(selected.bullets) && (
                    <List dense disablePadding>
                      {selected.bullets.map((point, idx) => (
                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', mr: 2 }} />
                          <ListItemText primary={point} primaryTypographyProps={{ fontSize: '1rem', fontWeight: 500 }} />
                        </ListItem>
                      ))}
                    </List>
                  )}
              </Box>

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => alert("Redirect to Contact")}
                sx={{ borderRadius: 50, py: 1.5, fontSize: '1rem', fontWeight: 700 }}
              >
                {t('products.modal.enquire')}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      
      <SolutionsPage/>
    </Box>
  );
};

export default ProductPage;
